/** The "every N hours" intervals the schedule editor offers as presets. */
export const HOURLY_PRESET_PERIODS: ReadonlySet<number> = new Set([1, 2, 4, 6, 8, 12]);

/**
 * The canonical care-plan timing shapes, one per schedule-editor option.
 * See docs/design-decisions/2026-07-01-canonical-care-plan-timing-shapes.md
 * for the full decision, including the disjointness rules and the shapes
 * `nonCanonical` names for the data audit.
 *
 * - `daysOfWeek` — period-1 daily cadence, optionally filtered to specific
 *   weekdays; `frequency` = times per day.
 * - `daysPerWeek` — weekly quota: complete on any N distinct days per week.
 * - `hourly` — "every N hours" editor presets.
 * - `interval` — one occurrence every N days/weeks/months/years.
 * - `nonCanonical` — everything else (ambiguous legacy data, bug shapes).
 */
export type TimingShape = 'daysOfWeek' | 'daysPerWeek' | 'hourly' | 'interval' | 'nonCanonical';

/**
 * Structural subset of `Timing.repeat` the classifier reads. Wider than
 * `FhirTimingRepeat` (plain `string` fields, readonly arrays) so Medplum's
 * `TimingRepeat` and extension-decoded shapes classify without narrowing.
 */
export interface TimingShapeInput {
  readonly frequency?: number;
  readonly period?: number;
  readonly periodUnit?: string;
  readonly dayOfWeek?: readonly string[];
  readonly when?: readonly string[];
  readonly timeOfDay?: readonly string[];
}

/** Days-per-week quotas above this are unsatisfiable (one completion per day). */
export const MAX_DAYS_PER_WEEK = 7;

const INTERVAL_PERIOD_UNITS: ReadonlySet<string> = new Set(['d', 'wk', 'mo', 'a']);

const hasEntries = (values?: readonly string[]): boolean => !!values && values.length > 0;

const isPositiveInteger = (value: number): boolean => Number.isInteger(value) && value >= 1;

const hasAnyModifier = (repeat: TimingShapeInput): boolean =>
  hasEntries(repeat.dayOfWeek) || hasEntries(repeat.when) || hasEntries(repeat.timeOfDay);

function classifyHourly(repeat: TimingShapeInput, frequency: number, period: number): TimingShape {
  if (hasAnyModifier(repeat) || frequency !== 1) return 'nonCanonical';
  return HOURLY_PRESET_PERIODS.has(period) ? 'hourly' : 'nonCanonical';
}

function classifyWeekday(repeat: TimingShapeInput, frequency: number, periodUnit: string): TimingShape {
  // when and timeOfDay are alternative time-of-day expressions; carrying both
  // leaves the slot count ambiguous.
  if (hasEntries(repeat.when) && hasEntries(repeat.timeOfDay)) return 'nonCanonical';
  const slotsPerDay = hasEntries(repeat.when)
    ? repeat.when!.length
    : hasEntries(repeat.timeOfDay)
      ? repeat.timeOfDay!.length
      : undefined;
  if (slotsPerDay !== undefined && frequency !== slotsPerDay) return 'nonCanonical';
  // On the legacy 'wk' encoding a bare frequency > 1 meant "N times per
  // week", not N times per day — ambiguous, so it goes to the audit.
  if (periodUnit === 'wk' && slotsPerDay === undefined && frequency > 1) return 'nonCanonical';
  return 'daysOfWeek';
}

function classifyQuotaOrInterval(
  repeat: TimingShapeInput,
  frequency: number,
  period: number,
  periodUnit: string,
): TimingShape {
  // Quota multiplicity is structurally capped at one completion per day, so
  // any number of `when` codes stays advisory here; on intervals more than
  // one would contradict the single event per period, so those cap at one.
  if (periodUnit === 'wk' && period === 1 && frequency >= 2) {
    return frequency <= MAX_DAYS_PER_WEEK ? 'daysPerWeek' : 'nonCanonical';
  }
  if (frequency === 1 && INTERVAL_PERIOD_UNITS.has(periodUnit)) {
    return (repeat.when?.length ?? 0) <= 1 ? 'interval' : 'nonCanonical';
  }
  return 'nonCanonical';
}

/**
 * Names the canonical shape a `Timing.repeat` represents, or `nonCanonical`
 * when it matches none of them.
 *
 * The cadence trio (`frequency`, `period`, `periodUnit`) must be present —
 * every validated writer emits it (`requireCadenceTrio`), so a sparse shape is
 * legacy data the audit should surface, not something to guess defaults for.
 *
 * Degenerate overlaps resolve deterministically: `every 1 day` classifies as
 * `daysOfWeek` (daily, all days implied), `every 1 week` as `interval`, and
 * 7× per week as `daysPerWeek` — all equivalent schedules that the editor
 * normalizes on write.
 */
export function classifyTimingShape(repeat: TimingShapeInput): TimingShape {
  const { frequency, period, periodUnit } = repeat;
  if (frequency === undefined || period === undefined || periodUnit === undefined) return 'nonCanonical';
  if (!isPositiveInteger(frequency) || !isPositiveInteger(period)) return 'nonCanonical';

  if (periodUnit === 'h') return classifyHourly(repeat, frequency, period);

  // Weekday shape: canonical period-1 'd' cadence (dayOfWeek absent ⇒ every
  // day), or the legacy 'wk' + dayOfWeek encoding, which reads identically and
  // is rewritten to 'd' on save.
  if (period === 1 && (periodUnit === 'd' || (periodUnit === 'wk' && hasEntries(repeat.dayOfWeek)))) {
    return classifyWeekday(repeat, frequency, periodUnit);
  }

  // The quota and interval shapes may carry `when` codes as a time-of-day
  // annotation ("3 days per week (morning)", "every 2 weeks (evening)") —
  // they never affect multiplicity, so `frequency` keeps its shape meaning
  // (days per week / one per period). `dayOfWeek` and `timeOfDay` remain
  // weekday-shape-only.
  if (hasEntries(repeat.dayOfWeek) || hasEntries(repeat.timeOfDay)) return 'nonCanonical';

  return classifyQuotaOrInterval(repeat, frequency, period, periodUnit);
}

// ============================================================================
// Lenient reader predicates
// ============================================================================

/**
 * Reader defaults for sparse legacy data. Validated writers always emit the
 * full cadence trio (`requireCadenceTrio`), but older stored timings may omit
 * parts of it, and omission always meant the FHIR default: once, per one,
 * daily.
 */
const withReaderDefaults = (repeat: TimingShapeInput): TimingShapeInput => ({
  ...repeat,
  frequency: repeat.frequency ?? 1,
  period: repeat.period ?? 1,
  periodUnit: repeat.periodUnit ?? 'd',
});

/**
 * `classifyTimingShape` with the reader defaults applied first — for reading
 * stored data that may predate the validated writers.
 */
export function classifyTimingShapeLenient(repeat: TimingShapeInput): TimingShape {
  return classifyTimingShape(withReaderDefaults(repeat));
}

/**
 * True when a timing reads as the days-per-week quota shape ("complete on any
 * N distinct days per week"). Lenient (see `classifyTimingShapeLenient`) so
 * sparse legacy encodings (e.g. `{frequency: 3, periodUnit: 'wk'}` with no
 * `period`) are recognized — the sentence formatter and the day-view engine
 * must agree on which timings are quotas.
 */
export function isDaysPerWeekShape(repeat: TimingShapeInput): boolean {
  return classifyTimingShapeLenient(repeat) === 'daysPerWeek';
}

/**
 * True when a timing reads as an interval ("every N days/weeks/months/years").
 *
 * Deliberately a superset of the classifier's `interval`: it also accepts
 * non-canonical legacy data carrying a bare `frequency` (e.g. "3 times per
 * month"), which the day view expands as N synthetic slots distributed across
 * the period rather than failing closed (see `getExpectedSlotsForDate` in
 * `@liza/services`). The days-per-week quota is carved out — it renders as a
 * quota row — and a period-1 `'d'` cadence is the daily shape, not an
 * interval.
 */
export function isIntervalShape(repeat: TimingShapeInput): boolean {
  if (hasEntries(repeat.dayOfWeek) || hasEntries(repeat.timeOfDay)) return false;
  // A single `when` code is a time-of-day annotation on the interval's slot
  // (canonical per `classifyTimingShape`); multiple codes would multiply
  // events per period, so those still fall through to per-`when` expansion.
  if ((repeat.when?.length ?? 0) > 1) return false;
  if (isDaysPerWeekShape(repeat)) return false;
  const { periodUnit } = repeat;
  if (periodUnit === 'wk' || periodUnit === 'mo' || periodUnit === 'a') return true;
  return periodUnit === 'd' && (repeat.period ?? 1) >= 2;
}
